import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core'
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker'
import { PaginationInstance } from 'ngx-pagination'
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs'
import { debounceTime, map, skip, takeUntil, tap } from 'rxjs/operators'

import { Asset, TagFragment } from '../../../common/generated-types'
import { DataService } from '../../../data/providers/data.service'
import { ModalService } from '../../../providers/modal/modal.service'
import { Dialog } from '../../../providers/modal/modal.types'
import { NotificationService } from '../../../providers/notification/notification.service'
import { AssetGalleryComponent } from '../asset-gallery/asset-gallery.component'
import { AssetLike } from '../asset-gallery/asset-gallery.types'

type AssetType = 'IMAGE' | 'BINARY'

/**
 * @description
 * A dialog which allows the creation and selection of assets.
 *
 * @example
 * ```ts
 * selectAssets() {
 *   this.modalService
 *     .fromComponent(AssetPickerDialogComponent, {
 *         size: 'xl',
 *         locals: {
 *           type: 'IMAGE' // or 'BINARY'
 *         }
 *     })
 *     .subscribe(result => {
 *         if (result && result.length) {
 *             // ...
 *         }
 *     });
 * }
 * ```
 *
 * @docsCategory components
 */
@Component({
  selector: 'vdr-asset-picker-dialog',
  templateUrl: './asset-picker-dialog.component.html',
  styleUrls: ['./asset-picker-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AssetPickerDialogComponent
  implements OnInit, AfterViewInit, OnDestroy, Dialog<Asset[]>
{
  assets$: Observable<AssetLike[]>
  allTags$: Observable<TagFragment[]>
  paginationConfig: PaginationInstance = {
    currentPage: 1,
    itemsPerPage: 25,
    totalItems: 1
  }

  @ViewChild('assetGalleryComponent')
  private assetGalleryComponent: AssetGalleryComponent

  entityId: string
  entityType: string

  multiSelect = true
  initialTags: string[] = []
  initialSelection: Asset[] = []
  type: AssetType = 'IMAGE'
  accept: string = 'image/*'
  itemsPerPage: number | undefined
  loading$ = new BehaviorSubject<boolean>(false)

  resolveWith: (result?: Asset[]) => void
  selection: Asset[] = []
  searchTerm$ = new BehaviorSubject<string | undefined>(undefined)
  filterByTags$ = new BehaviorSubject<TagFragment[] | undefined>(undefined)
  uploading = false
  /** Các asset vừa upload, được prepend lên đầu gallery ngay lập tức */
  private newlyUploadedAssets$ = new BehaviorSubject<AssetLike[]>([])
  // private listQuery: QueryResult<GetAssetListQuery, GetAssetListQueryVariables>
  private listQuery: any
  private destroy$ = new Subject<void>()

  constructor(
    private dataService: DataService,
    private notificationService: NotificationService,
    private modalService: ModalService,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit() {
    // Sử dụng itemsPerPage từ locals nếu có, nếu không dùng default
    if (this.itemsPerPage !== undefined && this.itemsPerPage > 0) {
      this.paginationConfig.itemsPerPage = this.itemsPerPage
    }

    const useShared = !!this.entityId
    if (useShared) {
      this.listQuery = this.dataService.product.getAssetShared(
        this.paginationConfig.itemsPerPage,
        0,
        this.entityId,
        this.entityType
      )
    } else {
      this.listQuery = this.dataService.product.getAssetList(
        this.paginationConfig.itemsPerPage,
        0
      )
    }
    this.allTags$ = this.dataService.product
      .getTagList()
      .mapSingle((data) => data.tags.items)
    const responseField = useShared ? 'getAssetShared' : 'assets'
    const serverAssets$: Observable<AssetLike[]> = this.listQuery.stream$.pipe(
      tap(
        (result: any) =>
          (this.paginationConfig.totalItems =
            result?.[responseField]?.totalItems)
      ),
      map(
        (result: any) => (result?.[responseField]?.items || []) as AssetLike[]
      )
    )
    this.assets$ = combineLatest([
      this.newlyUploadedAssets$,
      serverAssets$
    ]).pipe(
      map(([newAssets, serverAssets]: [AssetLike[], AssetLike[]]) => {
        if (newAssets.length === 0) return serverAssets
        const serverWithoutNew = serverAssets.filter(
          (s: AssetLike) => !newAssets.find((n) => n.id === s.id)
        )
        return [...newAssets, ...serverWithoutNew]
      }),
      tap((assets: AssetLike[]) => {
        // Auto-select assets that have isSelected flag from API
        let preSelected = assets.filter((a: any) => a.isSelected)

        // Also preselect initialSelection if provided
        if (this.initialSelection && this.initialSelection.length > 0) {
          const initialIds = this.initialSelection.map((a) => a.id)
          const additionalSelected = assets.filter((a) =>
            initialIds.includes(a.id)
          )
          preSelected = [...preSelected, ...additionalSelected]
          // Remove duplicates
          preSelected = preSelected.filter(
            (item, index, self) =>
              index === self.findIndex((t) => t.id === item.id)
          )
        }

        if (preSelected.length > 0) {
          setTimeout(() => {
            this.assetGalleryComponent?.selectMultiple(preSelected)
            this.changeDetector.markForCheck()
          })
        }
      })
    )
    this.searchTerm$
      .pipe(skip(1), debounceTime(250), takeUntil(this.destroy$))
      .subscribe(() => {
        this.fetchPage(
          this.paginationConfig.currentPage,
          this.paginationConfig.itemsPerPage
        )
      })
    this.filterByTags$
      .pipe(skip(1), debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => {
        this.fetchPage(
          this.paginationConfig.currentPage,
          this.paginationConfig.itemsPerPage
        )
      })
  }

  ngAfterViewInit() {
    // Search input removed
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  pageChange(page: number) {
    this.paginationConfig.currentPage = page
    this.fetchPage(
      this.paginationConfig.currentPage,
      this.paginationConfig.itemsPerPage
    )
  }

  itemsPerPageChange(itemsPerPage: number) {
    this.paginationConfig.itemsPerPage = itemsPerPage
    this.fetchPage(
      this.paginationConfig.currentPage,
      this.paginationConfig.itemsPerPage
    )
  }

  cancel() {
    this.resolveWith()
  }

  select() {
    this.resolveWith(this.selection)
  }

  async createAssets(files: File[]) {
    if (files.length > 10) {
      this.notificationService.error(
        'Bạn chỉ được tải lên tối đa 10 ảnh cùng lúc'
      )
      return
    }
    if (files.length) {
      this.uploading = true
      try {
        const responses = await Promise.all(
          files.map((file) =>
            this.dataService.product.createAssets([file]).toPromise()
          )
        )
        const allCreatedAssets: AssetLike[] = []
        for (const res of responses) {
          if (!res) continue
          switch (res.createAssets?.[0]?.__typename) {
            case 'Asset':
              const assets = res.createAssets.filter(
                (a: any) => a.__typename === 'Asset'
              ) as AssetLike[]
              allCreatedAssets.push(...assets)
              break

            case 'MimeTypeError':
              this.notificationService.error(
                res.createAssets?.[0]?.message ||
                  _('asset.notify-create-assets-error')
              )
              break

            default:
              this.notificationService.error(
                _('asset.notify-create-assets-error')
              )
              break
          }
        }

        if (allCreatedAssets.length > 0) {
          // Prepend lên đầu gallery ngay lập tức và đánh dấu selected
          const currentNew = this.newlyUploadedAssets$.getValue()
          this.newlyUploadedAssets$.next([...allCreatedAssets, ...currentNew])
          // Select sau 1 tick để gallery kịp render các assets mới
          setTimeout(() => {
            this.assetGalleryComponent.selectMultiple(allCreatedAssets)
          })
          this.notificationService.success(
            _('asset.notify-create-assets-success')
          )
        }
      } catch (err) {
        this.notificationService.error(_('asset.notify-create-assets-error'))
      } finally {
        this.uploading = false
      }
    }
  }

  private fetchPage(currentPage: number, itemsPerPage: number) {
    this.loading$.next(true)
    const take = +itemsPerPage
    const skip = (currentPage - 1) * +itemsPerPage

    const refetchVars = this.entityId
      ? {
          input: {
            take,
            skip,
            entityId: this.entityId,
            entityType: this.entityType
          }
        }
      : {
          options: {
            skip,
            take,
            sort: { createdAt: 'DESC' }
          }
        }

    this.listQuery.ref.refetch(refetchVars).then(() => {
      this.loading$.next(false)
    })
  }

  deleteSelectedAssets(assets: AssetLike[]) {
    if (!this.entityId) {
      this.notificationService.error(
        'Không thể xóa ảnh khi chưa có thông tin khách sạn'
      )
      return
    }

    const assetIds = assets.map((a) => a.id)

    this.modalService
      .dialog({
        title: 'Xác nhận xóa ảnh',
        body: `Bạn có chắc muốn xóa ${assetIds.length} ảnh đã chọn?`,
        buttons: [
          { type: 'secondary', label: 'Hủy' },
          { type: 'danger', label: 'Xóa', returnValue: true }
        ]
      })
      .subscribe((result) => {
        if (result) {
          this.loading$.next(true)
          this.dataService.product
            .deleteAssetShared(this.entityId, assetIds, this.entityType)
            .subscribe({
              next: (response: any) => {
                if (response.deleteSharedAsset?.success) {
                  this.notificationService.success('Xóa hình ảnh thành công!')
                  // Làm mới danh sách
                  this.fetchPage(
                    this.paginationConfig.currentPage,
                    this.paginationConfig.itemsPerPage
                  )
                  // Clear selection
                  this.selection = []
                  this.assetGalleryComponent.selectionManager.clearSelection()
                } else {
                  this.notificationService.error(
                    response.deleteSharedAsset?.message ||
                      _('common.notify-delete-error')
                  )
                  this.loading$.next(false)
                }
              },
              error: (err) => {
                console.error('Error deleting assets:', err)
                this.notificationService.error(_('common.notify-delete-error'))
                this.loading$.next(false)
              }
            })
        }
      })
  }
}
